import React, { useEffect, useState } from 'react';
import { unzip, zip, strToU8, strFromU8 } from 'fflate';
import { saveAs } from 'file-saver';
// ブラウザネイティブのDOMParser/XMLSerializerを使用（xmldomより高速）
import { Button } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorDialog from './ErrorDialog';
import SuccessDialog from './SuccessDialog';
import LoadingDialog from './LoadingDialog';
import { Messages } from '../constants/Messages';

interface DownloadWordProps {
    uploadedFiles: FileList | null;
    keywords: string[];
    excludedKeywords: string[];
    mergeStatus: string;
}
interface KeywordPosition {
    start: number;
    end: number;
    isExcluded: boolean;
}

const NAMESPACES = {
    W: 'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
    XML: 'http://www.w3.org/XML/1998/namespace'
} as const;

const DownloadWord: React.FC<DownloadWordProps> = ({
    uploadedFiles,
    keywords,
    excludedKeywords,
    mergeStatus,
}) => {
    const [files, setFile] = useState<FileList | null>(null);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setFile(uploadedFiles);
        setIsButtonDisabled(true);
    }, [uploadedFiles]);

    useEffect(() => {
        if (mergeStatus === 'completed') {
            setIsButtonDisabled(false);
        } else {
            setIsButtonDisabled(true);
        }
    }, [mergeStatus]);

    const unzipFile = (data: Uint8Array): Promise<Record<string, Uint8Array>> => {
        return new Promise((resolve, reject) => {
            unzip(data, (err, result) => {
                if (err) reject(err);
                else resolve(result);
            });
        });
    };

    const zipFiles = (files: Record<string, Uint8Array>): Promise<Uint8Array> => {
        return new Promise((resolve, reject) => {
            zip(files, { level: 1 }, (err, result) => {
                if (err) reject(err);
                else resolve(result);
            });
        });
    };

    const handleDownload = async () => {
        if (!files || files.length === 0) return;

        setIsLoading(true);
        try {
            // 全ファイルを並列処理
            const processedFiles = await Promise.all(
                Array.from(files).map(async (file) => {
                    const arrayBuffer = await file.arrayBuffer();
                    const data = new Uint8Array(arrayBuffer);
                    const unzippedFiles = await unzipFile(data);
                    if (!unzippedFiles['word/document.xml']) {
                        throw new Error(`${file.name}: word/document.xml が見つかりません`);
                    }
                    const xmlContent = strFromU8(unzippedFiles['word/document.xml']);

                    const updatedXmlContent = updateXmlContent(xmlContent, keywords, excludedKeywords);

                    unzippedFiles['word/document.xml'] = strToU8(updatedXmlContent);
                    const zipped = await zipFiles(unzippedFiles);
                    const blob = new Blob([zipped], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });

                    return { name: `highlighted_${file.name}`, blob };
                })
            );

            if(files.length === 1) {
                processedFiles.forEach(({ name, blob }) => {
                    saveAs(blob, name);
                });
            } else {
                // 複数ファイルをまとめてZIP化
                const zipEntries: Record<string, Uint8Array> = {};
                for (const { name, blob } of processedFiles) {
                    zipEntries[name] = new Uint8Array(await blob.arrayBuffer());
                }
                const finalZipped = await zipFiles(zipEntries);
                const finalBlob = new Blob([finalZipped], { type: 'application/zip' });
                saveAs(finalBlob, "highlighted_files.zip");
            }

            setSuccessDialogOpen(true);
        } catch (error) {
            setErrorMessage(`${Messages.FILE_PROCESS_ERROR}${error}`);
            setErrorDialogOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const createRun = (
        xmlDoc: Document,
        runElm: Element,
        text: string,
        highlight: boolean,
        excluded: boolean
    ): Element => {
        const newRun = xmlDoc.createElementNS(NAMESPACES.W, 'w:r');

        // rPrをコピー（存在する場合）
        const originalRPr = runElm.getElementsByTagName('w:rPr')[0];
        if (originalRPr) {
            const newRPr = originalRPr.cloneNode(true) as Element;

            if (highlight) {
                // 既存のハイライトを削除
                const existingHighlights = newRPr.getElementsByTagName('w:highlight');
                Array.from(existingHighlights).forEach(h => newRPr.removeChild(h));

                // 新しいハイライトを追加
                const highlightNode = xmlDoc.createElementNS(NAMESPACES.W, 'w:highlight');
                highlightNode.setAttribute('w:val', excluded ? 'cyan' : 'yellow');
                newRPr.appendChild(highlightNode);
            }

            newRun.appendChild(newRPr);
        } else if (highlight) {
            // rPrが存在しない場合は新規作成
            const rPr = xmlDoc.createElementNS(NAMESPACES.W, 'w:rPr');
            const highlightNode = xmlDoc.createElementNS(NAMESPACES.W, 'w:highlight');
            highlightNode.setAttribute('w:val', excluded ? 'cyan' : 'yellow');
            rPr.appendChild(highlightNode);
            newRun.appendChild(rPr);
        }

        // テキストノードを作成
        const textNode = xmlDoc.createElementNS(NAMESPACES.W, 'w:t');

        // スペースの保持
        if (text.startsWith(' ') || text.endsWith(' ') || /\s{2,}/.test(text)) {
            textNode.setAttributeNS(NAMESPACES.XML, 'xml:space', 'preserve');
        }

        textNode.textContent = text;
        newRun.appendChild(textNode);

        return newRun;
    };

    const updateXmlContent = (
        xml: string,
        keywords: string[],
        excludedKeywords: string[]
    ): string => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, 'application/xml');
        const parseError = xmlDoc.getElementsByTagName('parsererror');
        if (parseError.length > 0) {
            throw new Error('XMLの解析に失敗しました');
        }
        const paragraphs = Array.from(xmlDoc.getElementsByTagName('w:p'));

        // 正規表現の構築
        const allKeywords = [...excludedKeywords, ...keywords];
        if (allKeywords.length === 0) return xml;

        const pattern = allKeywords
            .map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // エスケープ
            .join('|');
        const regex = new RegExp(pattern, 'gi');

        paragraphs.forEach(paragraph => {
            processParagraph(xmlDoc, paragraph, regex, keywords, excludedKeywords);
        });

        const serializer = new XMLSerializer();
        return serializer.serializeToString(xmlDoc);
    };

    const processParagraph = (
        xmlDoc: Document,
        paragraph: Element,
        regex: RegExp,
        keywords: string[],
        excludedKeywords: string[]
    ): void => {
        // w:r内のw:tテキストのみ収集（paragraph.textContentは非w:r要素のテキストも含むためcurrentPosとずれる）
        let fullText = '';
        Array.from(paragraph.childNodes).forEach(node => {
            if (node.nodeName === 'w:r') {
                const textNodes = Array.from((node as Element).getElementsByTagName('w:t'));
                textNodes.forEach(t => {
                    fullText += t.textContent || '';
                });
            }
        });

        // キーワード位置を特定
        const positions: KeywordPosition[] = [];
        let match;
        regex.lastIndex = 0; // 正規表現をリセット

        while ((match = regex.exec(fullText)) !== null) {
            const matchedText = match[0];
            const isExcluded = excludedKeywords.some(
                k => k.toLowerCase() === matchedText.toLowerCase()
            );

            positions.push({
                start: match.index,
                end: match.index + matchedText.length,
                isExcluded: isExcluded
            });
        }

        if (positions.length === 0) return;

        // Run要素を処理
        const newNodes: Node[] = [];
        let currentPos = 0;

        Array.from(paragraph.childNodes).forEach(node => {
            if (node.nodeName === 'w:r') {
                const runElm = node as Element;
                const textNodes = Array.from(runElm.getElementsByTagName('w:t'));

                textNodes.forEach(textNode => {
                    const text = textNode.textContent || '';
                    const textStart = currentPos;
                    const textEnd = currentPos + text.length;

                    // このテキストノードに関連する位置情報を取得
                    const relevantPositions = positions.filter(
                        pos => pos.start < textEnd && pos.end > textStart
                    );

                    if (relevantPositions.length === 0) {
                        // ハイライト不要
                        newNodes.push(createRun(xmlDoc, runElm, text, false, false));
                    } else {
                        // テキストを分割してハイライト
                        splitTextWithHighlights(
                            xmlDoc,
                            runElm,
                            text,
                            textStart,
                            relevantPositions,
                            newNodes
                        );
                    }

                    currentPos = textEnd;
                });
            } else {
                newNodes.push(node.cloneNode(true));
            }
        });

        // 段落の子要素を置き換え
        while (paragraph.firstChild) {
            paragraph.removeChild(paragraph.firstChild);
        }
        newNodes.forEach(node => paragraph.appendChild(node));
    };

    const splitTextWithHighlights = (
        xmlDoc: Document,
        runElm: Element,
        text: string,
        textStart: number,
        positions: KeywordPosition[],
        outputNodes: Node[]
    ): void => {
        let lastIndex = 0;

        positions.forEach(pos => {
            const relStart = Math.max(0, pos.start - textStart);
            const relEnd = Math.min(text.length, pos.end - textStart);

            // ハイライト前のテキスト
            if (relStart > lastIndex) {
                outputNodes.push(
                    createRun(
                        xmlDoc,
                        runElm,
                        text.substring(lastIndex, relStart),
                        false,
                        false
                    )
                );
            }

            // ハイライト部分
            if (relEnd > relStart) {
                outputNodes.push(
                    createRun(
                        xmlDoc,
                        runElm,
                        text.substring(relStart, relEnd),
                        true,
                        pos.isExcluded
                    )
                );
            }

            lastIndex = relEnd;
        });

        // 残りのテキスト
        if (lastIndex < text.length) {
            outputNodes.push(
                createRun(
                    xmlDoc,
                    runElm,
                    text.substring(lastIndex),
                    false,
                    false
                )
            );
        }
    };

    return (
        <div className='step-accordion'>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                >
                    <h3>STEP3: 検索キーワードをハイライトしたWordファイルをダウンロードしてください。</h3>
                </AccordionSummary>
                <AccordionDetails>
                    <div className='button-wrapper'>
                        <Button
                            className='analyze-button'
                            onClick={handleDownload}
                            disabled={isButtonDisabled}
                            variant='contained'>
                            Wordファイルをダウンロード
                        </Button>
                    </div>
                    <div className='caution-message'>
                       ※ダウンロードが完了するまでに時間がかかることがあります
                    </div>
                </AccordionDetails>
            </Accordion>
            <ErrorDialog open={errorDialogOpen} errorMessage={errorMessage} onClose={() => setErrorDialogOpen(false)} />
            <SuccessDialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} message={Messages.DL_WORD} />
            <LoadingDialog open={isLoading} message="" />
        </div>
    );
};

export default DownloadWord;
